import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { useVendors } from '@/hooks/useVendors'
import { cn } from '@/lib/utils'

// Session 7.5 — one PO has one recipient, either a supplier (umbrella over
// several vendor brands) or a single vendor. Most vendors don't have a
// supplier configured, so vendor-only is the common case; supplier is the
// special path that unlocks multi-vendor POs. A unified picker keeps the
// Step 1 layout to a single control while still surfacing both options.

export type RecipientKind = 'supplier' | 'vendor'

export interface RecipientSelection {
  kind: RecipientKind
  value: string
}

interface Props {
  supplier: string
  vendor: string
  onChange: (sel: RecipientSelection) => void
  disabled?: boolean
  id?: string
}

export function RecipientCombobox({
  supplier,
  vendor,
  onChange,
  disabled,
  id,
}: Props) {
  const [open, setOpen] = React.useState(false)
  const { data: vendors = [], isLoading } = useVendors()

  const suppliers = React.useMemo(() => {
    const set = new Set<string>()
    for (const v of vendors) {
      const s = (v.supplier ?? '').trim()
      if (s) set.add(s)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [vendors])

  const vendorNames = React.useMemo(
    () => vendors.map((v) => v.vendor).sort((a, b) => a.localeCompare(b)),
    [vendors]
  )

  const buttonLabel =
    supplier || vendor || (isLoading ? 'Loading recipients…' : 'Select supplier or vendor…')
  const buttonHint =
    supplier ? 'Supplier' : vendor ? 'Vendor' : null

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className="flex items-center gap-2 truncate">
            {buttonHint && (
              <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                {buttonHint}
              </span>
            )}
            <span className="truncate">{buttonLabel}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command
          filter={(value, search) => {
            // The CommandItem `value` is prefixed (e.g. `s::Jacks`) so we
            // strip the prefix before matching against the user's query.
            const stripped = value.replace(/^(?:s|v)::/, '').toLowerCase()
            return stripped.includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Search suppliers or vendors…" />
          <CommandList>
            <CommandEmpty>No recipient found.</CommandEmpty>
            {suppliers.length > 0 && (
              <CommandGroup heading="Suppliers (multi-brand)">
                {suppliers.map((s) => (
                  <CommandItem
                    key={`s::${s}`}
                    value={`s::${s}`}
                    onSelect={() => {
                      onChange({ kind: 'supplier', value: s })
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        supplier === s ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {s}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {suppliers.length > 0 && <CommandSeparator />}
            <CommandGroup heading="Vendors (single brand)">
              {vendorNames.map((v) => (
                <CommandItem
                  key={`v::${v}`}
                  value={`v::${v}`}
                  onSelect={() => {
                    onChange({ kind: 'vendor', value: v })
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      vendor === v ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {v}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
